import { FC } from "react";
import { FormattedMessage } from "react-intl";
import {
  Button,
  Grid,
  IconButton,
  List,
  ListItem,
  ListItemSecondaryAction,
  ListItemText,
  Pagination,
} from "@mui/material";
import { Add, Create, Delete } from "@mui/icons-material";

import { Breadcrumbs, PageHeader, ProgressOverlay } from "@dzambalaorg/mui-page-layout";
import { DeleteDialog } from "@dzambalaorg/mui-dialog-delete";
import type { IPage, IPageSearchDto } from "@framework/types";
import { useCollection, CollectionActions } from "@dzambalaorg/provider-collection";
import { emptyStateString } from "@dzambalaorg/draft-js-utils";

import { PageEditDialog } from "./edit";

export const Page: FC = () => {
  const {
    rows,
    count,
    search,
    action,
    selected,
    isLoading,
    handleCreate,
    handleEdit,
    handleEditCancel,
    handleEditConfirm,
    handleDelete,
    handleDeleteCancel,
    handleChangePage,
    handleDeleteConfirm,
  } = useCollection<IPage, IPageSearchDto>({
    baseUrl: "/pages",
    empty: {
      title: "",
      description: emptyStateString,
      slug: "",
    },
    search: {
      query: "",
    },
    filter: ({ id: _id, ...rest }) => rest,
  });

  return (
    <Grid>
      <Breadcrumbs path={["dashboard", "pages"]} />

      <PageHeader message="pages.pages.title">
        <Button variant="outlined" startIcon={<Add />} onClick={handleCreate} data-testid="PageCreateButton">
          <FormattedMessage id="form.buttons.create" />
        </Button>
      </PageHeader>

      <ProgressOverlay isLoading={isLoading}>
        <List>
          {rows.map(page => (
            <ListItem key={page.id}>
              <ListItemText>{page.title}</ListItemText>
              <ListItemSecondaryAction>
                <IconButton onClick={handleEdit(page)}>
                  <Create />
                </IconButton>
                <IconButton onClick={handleDelete(page)}>
                  <Delete />
                </IconButton>
              </ListItemSecondaryAction>
            </ListItem>
          ))}
        </List>
      </ProgressOverlay>

      <Pagination
        sx={{ mt: 2 }}
        shape="rounded"
        page={search.skip / search.take + 1}
        count={Math.ceil(count / search.take)}
        onChange={handleChangePage}
      />

      <DeleteDialog
        onCancel={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
        open={action === CollectionActions.delete}
        initialValues={selected}
      />

      <PageEditDialog
        onCancel={handleEditCancel}
        onConfirm={handleEditConfirm}
        open={action === CollectionActions.edit}
        initialValues={selected}
      />
    </Grid>
  );
};
